import { Services } from '@project/API';
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from 'mobx';
import {
  KilometersSearchParams,
  DEFAULT_TABLE_NAME,
} from '@project/API/resources/geojson';
import { SearchItem } from '@project/API/resources/tableSearch';
import { ResultNode } from './GlobalSearch/ResultNode';
import { MapProperties } from '@datum-group/datum-map/lib';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { Icon, Style } from 'ol/style';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Coordinate } from 'ol/coordinate';
import image from '@assets/images/map-controls/place.svg';
import { ZINDEX } from '@project/components/Map/constants';
import { Layer } from 'ol/layer';

export class RoadSectionSearch {
  private _services: Services;
  private _mapProperties: MapProperties;
  @observable private _params?: KilometersSearchParams = undefined;
  @observable private _results: ResultNode[] = [];
  @observable private _error?: string = undefined;
  @observable private _isLoading: boolean = false;
  private markerLayer: Layer | undefined;

  constructor(services: Services, mapProperties: MapProperties) {
    this._mapProperties = mapProperties;
    this._services = services;
    this.markerLayer = undefined;
    makeObservable(this);
  }

  @computed get error() {
    return this._error;
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get results() {
    return this._results;
  }

  @computed get params() {
    return this._params;
  }

  @action setParams(newParams: KilometersSearchParams) {
    this._params = newParams;
  }

  @action loadData = async () => {
    runInAction(() => {
      this._error = undefined;
      this._isLoading = true;
      this._results = [];
    });

    try {
      if (!this._params) {
        return;
      }

      const response = await this._services.geojsonSearch.list(this._params);
      const resultNodeList: ResultNode[] = response.features.map((feature) => {
        const tableSearchItem: SearchItem = {
          ...feature,
          table_name: DEFAULT_TABLE_NAME,
        };

        return new ResultNode(tableSearchItem);
      });
      runInAction(() => {
        this._results = resultNodeList;
      });
    } catch (e) {
      runInAction(() => (this._error = 'Не удалось загрузить страницу'));
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  };

  showOnMap = (coords: Coordinate) => {
    this.removeMarker();

    this._mapProperties.map.getView().animate({
      center: coords,
      zoom: 14,
    });

    const marker = new Feature({
      geometry: new Point(coords),
    });

    const icon = new Style({
      image: new Icon({
        anchor: [0.5, 40],
        anchorXUnits: 'fraction',
        anchorYUnits: 'pixels',
        src: image,
      }),
    });
    marker.setStyle(icon);

    const vectorSource = new VectorSource({
      features: [marker],
    });

    this.markerLayer = new VectorLayer({
      source: vectorSource,
    });

    this.markerLayer.setZIndex(ZINDEX.ROUTE_POINT);

    this._mapProperties.map.addLayer(this.markerLayer);
  };

  removeMarker = () => {
    if (this.markerLayer) {
      this._mapProperties.map.removeLayer(this.markerLayer);
    }
  };
}
